import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../../lib/supabaseClient";

function getInitials(first, last) {
  return `${first?.[0] || ""}${last?.[0] || ""}`.toUpperCase();
}

function getLocationGroup(groups) {
  return (groups || []).find((g) => /\d/.test(g)) || groups?.[0] || "—";
}

function getPctStyle(pct) {
  if (pct === 100) return { badge: "bg-emerald-100 text-emerald-700", bar: "bg-emerald-500" };
  if (pct >= 50)   return { badge: "bg-amber-100 text-amber-700",     bar: "bg-amber-500"   };
  return                  { badge: "bg-red-100 text-red-600",         bar: "bg-red-400"     };
}

function exportCSV(teamMembers, enrolmentMap, courseMap) {
  const rows = [["User", "Branch", "Active User", "Course Name", "Status"]];
  teamMembers.forEach((member) => {
    const fullName = `${member.first_name} ${member.last_name}`;
    const branch = getLocationGroup(member.groups);
    const activeUser = member.active === false ? "No" : "Yes";
    const memberEnrols = enrolmentMap[member.user_id] || {};
    if (Object.keys(memberEnrols).length === 0) {
      rows.push([fullName, branch, activeUser, "No courses assigned", ""]);
    } else {
      Object.entries(memberEnrols).forEach(([courseId, e]) => {
        rows.push([fullName, branch, activeUser, courseMap[courseId]?.title || courseId, e.completed ? "Completed" : "Assigned"]);
      });
    }
  });
  const csv = rows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `team-report-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

export default function Team() {
  const [loading, setLoading] = useState(true);
  const [teamMembers, setTeamMembers] = useState([]);
  const [enrolmentMap, setEnrolmentMap] = useState({});
  const [courseMap, setCourseMap] = useState({});
  const [managerGroups, setManagerGroups] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [statModal, setStatModal] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [groupFilter, setGroupFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("outstanding");
  const [noGroupsAssigned, setNoGroupsAssigned] = useState(false);
  const [view, setView] = useState("members");
  const [activeOnly, setActiveOnly] = useState(true);

  const loadTeamData = useCallback(async () => {
    setLoading(true);
    const { data: userRes } = await supabase.auth.getUser();
    const user = userRes?.user;
    if (!user?.id) { setLoading(false); return; }

    const { data: mgData } = await supabase
      .from("manager_groups").select("group_name").eq("manager_id", user.id);
    const myGroups = (mgData || []).map((r) => r.group_name);
    setManagerGroups(myGroups);

    if (myGroups.length === 0) { setNoGroupsAssigned(true); setLoading(false); return; }

    const { data: teamData, error: teamError } = await supabase.rpc("get_my_team");
    if (teamError) { console.error("get_my_team error:", teamError); setLoading(false); return; }

    const members = teamData || [];
    setTeamMembers(members);
    if (members.length === 0) { setLoading(false); return; }

    const memberIds = members.map((m) => m.user_id);
    const { data: enrolData, error: enrolError } = await supabase
      .rpc("get_team_enrolments", { member_ids: memberIds });
    if (enrolError) console.error("Enrolments error:", enrolError);

    const cMap = {};
    (enrolData || []).forEach((e) => {
      if (e.course_title && !cMap[e.course_id])
        cMap[e.course_id] = { id: e.course_id, title: e.course_title };
    });
    setCourseMap(cMap);

    const map = {};
    memberIds.forEach((id) => { map[id] = {}; });
    (enrolData || []).forEach((e) => {
      if (!map[e.user_id]) map[e.user_id] = {};
      map[e.user_id][e.course_id] = { completed: e.completed, completed_at: e.completed_at };
    });
    setEnrolmentMap(map);
    setLoading(false);
  }, []);

  useEffect(() => { loadTeamData(); }, [loadTeamData]);

  function getMemberSummary(userId) {
    const memberEnrols = enrolmentMap[userId] || {};
    let completed = 0, assigned = 0;
    Object.values(memberEnrols).forEach((e) => { if (e.completed) completed++; else assigned++; });
    return { completed, assigned, total: completed + assigned };
  }

  const visibleMembers = activeOnly
    ? teamMembers.filter((m) => m.active !== false)
    : teamMembers;

  const totalMembers = visibleMembers.length;

  // Not Started: has courses assigned but 0% (completed === 0, assigned > 0)
  const notStartedMembers = visibleMembers.filter((m) => {
    const s = getMemberSummary(m.user_id);
    return s.total > 0 && s.completed === 0;
  });

  // In Progress: has started but not finished (completed > 0, assigned > 0)
  const inProgressMembers = visibleMembers.filter((m) => {
    const s = getMemberSummary(m.user_id);
    return s.completed > 0 && s.assigned > 0;
  });

  // All Complete: every assigned course is done (total > 0, assigned === 0)
  const allCompleteMembers = visibleMembers.filter((m) => {
    const s = getMemberSummary(m.user_id);
    return s.total > 0 && s.assigned === 0;
  });

  // Outstanding (used by existing filter pill) = not started + in progress
  const outstandingMembers = visibleMembers.filter((m) => getMemberSummary(m.user_id).assigned > 0);

  const courseStats = Object.values(courseMap).map((course) => {
    let enrolled = 0, completed = 0;
    const members = [];
    visibleMembers.forEach((m) => {
      const e = enrolmentMap[m.user_id]?.[course.id];
      if (e) {
        enrolled++;
        if (e.completed) completed++;
        members.push({ ...m, completed: e.completed, completed_at: e.completed_at });
      }
    });
    const pct = enrolled > 0 ? Math.round((completed / enrolled) * 100) : 0;
    return { ...course, enrolled, completed, pct, members };
  }).sort((a, b) => a.pct - b.pct);

  const filteredMembers = visibleMembers.filter((m) => {
    const name = `${m.first_name} ${m.last_name} ${m.email}`.toLowerCase();
    if (!name.includes(searchTerm.toLowerCase())) return false;
    if (groupFilter !== "all" && !(m.groups || []).includes(groupFilter)) return false;
    if (statusFilter === "all") return true;
    const s = getMemberSummary(m.user_id);
    if (statusFilter === "outstanding") return s.assigned > 0;
    if (statusFilter === "completed") return s.assigned === 0 && s.total > 0;
    return true;
  });

  // Stat modal config — order: Total → Not Started → In Progress → All Complete
  const statModalConfig = {
    all:         { title: "All Members",  members: visibleMembers,     accent: "text-gray-900" },
    notstarted:  { title: "Not Started",  members: notStartedMembers,  accent: "text-red-500" },
    inprogress:  { title: "In Progress",  members: inProgressMembers,  accent: "text-amber-500" },
    complete:    { title: "All Complete", members: allCompleteMembers, accent: "text-emerald-600" },
  };

  function renderSubtitle() {
    const groupCount = managerGroups.length;
    if (groupCount === 0) return null;
    if (groupCount > 6) return <span className="ml-1">· {totalMembers} member{totalMembers !== 1 ? "s" : ""} · {groupCount} groups</span>;
    return <span className="ml-1">· {managerGroups.join(", ")}</span>;
  }

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-800" />
      </div>
    );
  }

  if (noGroupsAssigned) {
    return (
      <div className="p-8 max-w-5xl mx-auto">
        <div className="bg-white shadow-md rounded-2xl p-8 text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">My Team</h1>
          <p className="text-gray-500">No groups have been assigned to your account yet. Contact your administrator.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">

      {/* Stats bar — Total | Not Started | In Progress | All Complete */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { key: "all",        label: "Total Members", value: totalMembers,             valueClass: "text-gray-900" },
          { key: "notstarted", label: "Not Started",   value: notStartedMembers.length, valueClass: "text-red-500" },
          { key: "inprogress", label: "In Progress",   value: inProgressMembers.length, valueClass: "text-amber-500" },
          { key: "complete",   label: "All Complete",  value: allCompleteMembers.length, valueClass: "text-emerald-600" },
        ].map(({ key, label, value, valueClass }) => (
          <button key={key} onClick={() => setStatModal(key)}
            className="bg-white rounded-xl shadow-md p-4 text-center hover:shadow-lg hover:bg-gray-50 transition">
            <p className={`text-2xl font-bold ${valueClass}`}>{value}</p>
            <p className="text-xs text-gray-400 mt-0.5">{label}</p>
          </button>
        ))}
      </div>

      {/* Main panel */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="px-6 pt-6 pb-0 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">My Team</h1>
            <p className="text-sm text-gray-400 mt-0.5">
              {view === "members" ? `${filteredMembers.length} of ` : ""}{totalMembers} members{renderSubtitle()}
            </p>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-3">
              <div>
                <p className="text-sm font-medium text-gray-700">Active users only</p>
                <p className="text-xs text-gray-400">{activeOnly ? "Disabled users hidden" : "Showing disabled users"}</p>
              </div>
              <button
                onClick={() => setActiveOnly((prev) => !prev)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                  activeOnly ? "bg-green-500" : "bg-gray-200"
                }`}
                style={activeOnly ? { backgroundColor: "#8CC63F" } : {}}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                  activeOnly ? "translate-x-6" : "translate-x-1"
                }`} />
              </button>
            </div>

            <button
              onClick={() => exportCSV(visibleMembers, enrolmentMap, courseMap)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Export CSV
            </button>
          </div>
        </div>

        <div className="flex border-b border-gray-100 mt-4 px-6">
          {[{ key: "members", label: "Members" }, { key: "courses", label: "Courses" }].map(({ key, label }) => (
            <button key={key} onClick={() => setView(key)}
              className={`py-3 pr-8 text-sm font-semibold border-b-2 transition ${
                view === key ? "border-gray-900 text-gray-900" : "border-transparent text-gray-400 hover:text-gray-600"
              }`}>
              {label}
            </button>
          ))}
        </div>

        {/* Members tab */}
        {view === "members" && (
          <>
            <div className="px-6 py-4 border-b border-gray-50 flex flex-wrap items-center gap-3">
              <div className="flex gap-2">
                {[{ key: "all", label: "All" }, { key: "outstanding", label: "Outstanding" }, { key: "completed", label: "All Complete" }].map(({ key, label }) => (
                  <button key={key} onClick={() => setStatusFilter(key)}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${
                      statusFilter === key ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}>
                    {label}
                  </button>
                ))}
              </div>
              <div className="w-px h-6 bg-gray-200" />
              <input type="text" placeholder="Search team members..." value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 text-sm px-4 py-1.5 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-800 min-w-[160px]" />
              {managerGroups.length > 1 && (
                <select value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)}
                  className="text-sm px-3 py-1.5 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-800 text-gray-600">
                  <option value="all">All branches</option>
                  {managerGroups.map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
              )}
            </div>

            {filteredMembers.length === 0 ? (
              <div className="p-12 text-center text-gray-400">
                <p className="font-medium">No team members match your filters</p>
              </div>
            ) : (
              <div className="p-6 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {filteredMembers.map((member) => {
                  const summary = getMemberSummary(member.user_id);
                  const pct = summary.total > 0 ? Math.round((summary.completed / summary.total) * 100) : 0;
                  const style = getPctStyle(pct);
                  const noEnrolments = summary.total === 0;
                  return (
                    <button key={member.user_id} onClick={() => setSelectedMember(member)}
                      className="text-left bg-gray-50 rounded-xl p-5 hover:bg-gray-100 transition space-y-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 text-white ${noEnrolments ? "bg-gray-300" : "bg-gray-900"}`}>
                          {getInitials(member.first_name, member.last_name)}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">{member.first_name} {member.last_name}</p>
                          <p className="text-xs text-gray-400 truncate">{getLocationGroup(member.groups)}</p>
                        </div>
                        {noEnrolments ? (
                          <span className="ml-auto flex-shrink-0 text-sm font-medium px-2.5 py-1 rounded-full bg-gray-100 text-gray-400">—</span>
                        ) : (
                          <span className={`ml-auto flex-shrink-0 text-sm font-bold px-2.5 py-1 rounded-full ${style.badge}`}>{pct}%</span>
                        )}
                      </div>
                      {!noEnrolments && (
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                          <div className={`h-1.5 rounded-full transition-all ${style.bar}`} style={{ width: `${pct}%` }} />
                        </div>
                      )}
                      {noEnrolments ? (
                        <p className="text-xs text-gray-400 italic">No courses assigned</p>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          <span className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 font-medium">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />{summary.completed} completed
                          </span>
                          {summary.assigned > 0 && (
                            <span className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 font-medium">
                              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block" />{summary.assigned} assigned
                            </span>
                          )}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Courses tab */}
        {view === "courses" && (
          <>
            <div className="px-6 py-3 border-b border-gray-50">
              <p className="text-xs text-gray-400">Sorted by lowest completion first — click a course to see who's enrolled</p>
            </div>
            {courseStats.length === 0 ? (
              <div className="p-12 text-center text-gray-400 text-sm">No course data available</div>
            ) : (
              <div className="divide-y divide-gray-50">
                {courseStats.map((course) => {
                  const style = getPctStyle(course.pct);
                  return (
                    <button key={course.id} onClick={() => setSelectedCourse(course)}
                      className="w-full px-6 py-4 flex items-center gap-4 hover:bg-gray-50 transition text-left">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{course.title}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{course.completed} of {course.enrolled} enrolled members completed</p>
                      </div>
                      <div className="w-40 flex-shrink-0">
                        <div className="w-full bg-gray-100 rounded-full h-2">
                          <div className={`h-2 rounded-full transition-all ${style.bar}`} style={{ width: `${course.pct}%` }} />
                        </div>
                      </div>
                      <span className={`flex-shrink-0 text-sm font-bold px-2.5 py-1 rounded-full ${style.badge}`}>{course.pct}%</span>
                      <svg className="w-4 h-4 text-gray-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {statModal && (
        <StatModal title={statModalConfig[statModal].title} members={statModalConfig[statModal].members}
          accent={statModalConfig[statModal].accent} getMemberSummary={getMemberSummary} onClose={() => setStatModal(null)} />
      )}
      {selectedMember && (
        <MemberModal member={selectedMember} courseMap={courseMap}
          enrolments={enrolmentMap[selectedMember.user_id] || {}} onClose={() => setSelectedMember(null)} />
      )}
      {selectedCourse && (
        <CourseModal course={selectedCourse} onClose={() => setSelectedCourse(null)} />
      )}
    </div>
  );
}

function StatModal({ title, members, accent, getMemberSummary, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col">
        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
          <div>
            <p className={`text-lg font-bold ${accent}`}>{title}</p>
            <p className="text-xs text-gray-400 mt-0.5">{members.length} member{members.length !== 1 ? "s" : ""}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1.5 rounded-lg hover:bg-gray-100 transition">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {members.length === 0 && <p className="text-center text-sm text-gray-400 py-8">No members in this category</p>}
          {members.map((member) => {
            const summary = getMemberSummary(member.user_id);
            const pct = summary.total > 0 ? Math.round((summary.completed / summary.total) * 100) : 0;
            const noEnrolments = summary.total === 0;
            const style = getPctStyle(pct);
            return (
              <div key={member.user_id} className="flex items-center justify-between px-4 py-3 rounded-xl bg-gray-50">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0 ${noEnrolments ? "bg-gray-300" : "bg-gray-900"}`}>
                    {getInitials(member.first_name, member.last_name)}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{member.first_name} {member.last_name}</p>
                    <p className="text-xs text-gray-400 truncate">{getLocationGroup(member.groups)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                  {noEnrolments ? (
                    <span className="text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-400 font-medium">Not enrolled</span>
                  ) : (
                    <>
                      <span className="text-xs text-gray-400">{summary.completed}/{summary.total}</span>
                      <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${style.badge}`}>{pct}%</span>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function CourseModal({ course, onClose }) {
  const [tab, setTab] = useState("assigned");
  const style = getPctStyle(course.pct);
  const assigned = course.members.filter((m) => !m.completed);
  const completed = course.members.filter((m) => m.completed);
  const visible = tab === "assigned" ? assigned : completed;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col">
        <div className="p-5 border-b border-gray-100 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 leading-snug">{course.title}</p>
            <p className="text-xs text-gray-400 mt-1">{course.enrolled} enrolled · {course.completed} completed</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1.5 rounded-lg hover:bg-gray-100 transition flex-shrink-0">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="px-5 py-4 border-b border-gray-50">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-gray-500">{course.completed} of {course.enrolled} members completed</span>
            <span className={`text-sm font-bold px-2.5 py-1 rounded-full ${style.badge}`}>{course.pct}%</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2">
            <div className={`h-2 rounded-full transition-all ${style.bar}`} style={{ width: `${course.pct}%` }} />
          </div>
        </div>
        <div className="flex border-b border-gray-100">
          {[{ key: "assigned", label: `Not Completed (${assigned.length})` }, { key: "completed", label: `Completed (${completed.length})` }].map(({ key, label }) => (
            <button key={key} onClick={() => setTab(key)}
              className={`flex-1 py-3 text-sm font-medium transition border-b-2 ${tab === key ? "border-gray-900 text-gray-900" : "border-transparent text-gray-400 hover:text-gray-600"}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {visible.length === 0 && (
            <p className="text-center text-sm text-gray-400 py-8">
              {tab === "assigned" ? "Everyone has completed this course" : "Nobody has completed this course yet"}
            </p>
          )}
          {visible.map((member) => (
            <div key={member.user_id} className="flex items-center justify-between px-4 py-3 rounded-xl bg-gray-50">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-8 h-8 rounded-full bg-gray-900 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                  {getInitials(member.first_name, member.last_name)}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{member.first_name} {member.last_name}</p>
                  <p className="text-xs text-gray-400 truncate">{getLocationGroup(member.groups)}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                {member.completed_at && (
                  <span className="text-xs text-gray-400">
                    {new Date(member.completed_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}
                  </span>
                )}
                <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${member.completed ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                  {member.completed ? "Completed" : "Assigned"}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MemberModal({ member, courseMap, enrolments, onClose }) {
  const [tab, setTab] = useState("assigned");
  const allEnrolled = Object.entries(enrolments).map(([courseId, e]) => ({
    courseId, title: courseMap[courseId]?.title || courseId, completed: e.completed, completed_at: e.completed_at,
  }));
  const assigned = allEnrolled.filter((e) => !e.completed);
  const completed = allEnrolled.filter((e) => e.completed);
  const visibleCourses = tab === "assigned" ? assigned : completed;
  const total = allEnrolled.length;
  const pct = total > 0 ? Math.round((completed.length / total) * 100) : 0;
  const style = getPctStyle(pct);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col">
        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gray-900 text-white flex items-center justify-center text-sm font-bold">
              {getInitials(member.first_name, member.last_name)}
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">{member.first_name} {member.last_name}</p>
              <p className="text-xs text-gray-400">{member.email}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1.5 rounded-lg hover:bg-gray-100 transition">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {total === 0 ? (
          <div className="flex-1 flex items-center justify-center p-8 text-center">
            <p className="text-sm text-gray-400">No courses assigned to this team member.</p>
          </div>
        ) : (
          <>
            <div className="px-5 py-4 border-b border-gray-50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-500">{completed.length} of {total} courses completed</span>
                <span className={`text-sm font-bold px-2.5 py-1 rounded-full ${style.badge}`}>{pct}%</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2">
                <div className={`h-2 rounded-full transition-all ${style.bar}`} style={{ width: `${pct}%` }} />
              </div>
            </div>
            <div className="flex border-b border-gray-100">
              {[{ key: "assigned", label: `Assigned (${assigned.length})` }, { key: "completed", label: `Completed (${completed.length})` }].map(({ key, label }) => (
                <button key={key} onClick={() => setTab(key)}
                  className={`flex-1 py-3 text-sm font-medium transition border-b-2 ${tab === key ? "border-gray-900 text-gray-900" : "border-transparent text-gray-400 hover:text-gray-600"}`}>
                  {label}
                </button>
              ))}
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {visibleCourses.length === 0 && (
                <p className="text-center text-sm text-gray-400 py-8">
                  {tab === "assigned" ? "No outstanding courses" : "No completed courses yet"}
                </p>
              )}
              {visibleCourses.map((course) => (
                <div key={course.courseId} className="flex items-center justify-between px-4 py-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${course.completed ? "bg-emerald-500" : "bg-amber-500"}`} />
                    <p className="text-sm text-gray-800 font-medium truncate">{course.title}</p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                    {course.completed_at && (
                      <span className="text-xs text-gray-400">
                        {new Date(course.completed_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}
                      </span>
                    )}
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${course.completed ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                      {course.completed ? "Completed" : "Assigned"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
